import {
	Divider,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	useTheme,
} from "@mui/material";
import {
	CircleCheckBig,
	MoreHorizontal,
	RotateCcw,
	Trash2,
} from "lucide-react";
import { type FC, type MouseEvent, useState } from "react";
import { GoalActionDialogs } from "web_myplann/Goals/Components/GoalActionDialogs";
import { useGoalActions } from "web_myplann/Goals/Hooks/useGoalActions";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

const menuItemSx = { gap: 1.2, py: 1.2, px: 1.6 };

type Props = {
	goal: GoalDetail;
	goalId: number;
	onBlocked: () => void;
};

export const GoalDetailMenu: FC<Props> = ({ goal, goalId, onBlocked }) => {
	const theme = useTheme();
	const { t } = useResource();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	const actions = useGoalActions({
		goalId,
		allowEditByClient: goal.allowEditByClient,
		onBlocked,
	});

	const closeMenu = () => setAnchorEl(null);

	const handleAction = (next: "complete" | "reactivate" | "delete") => {
		closeMenu();
		actions.openDialog(next);
	};

	return (
		<>
			<IconButton
				variant="soft"
				aria-label={t(Resources.Goals.Detail.Actions.ariaLabel)}
				onClick={(event: MouseEvent<HTMLElement>) =>
					setAnchorEl(event.currentTarget)
				}
			>
				<MoreHorizontal size={theme.iconSize.sm} />
			</IconButton>
			<Menu
				anchorEl={anchorEl}
				open={anchorEl !== null}
				onClose={closeMenu}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				slotProps={{
					paper: { sx: { borderRadius: theme.radii.lg } },
					list: {
						sx: {
							p: 0,
							"&& .MuiDivider-root": { my: 0 },
							"&& .MuiListItemIcon-root": { minWidth: 0 },
							"& .MuiMenuItem-root": { backgroundColor: "background.default" },
						},
					},
				}}
			>
				{goal.completed ? (
					<MenuItem onClick={() => handleAction("reactivate")} sx={menuItemSx}>
						<ListItemIcon>
							<RotateCcw size={theme.iconSize.sm} />
						</ListItemIcon>
						<ListItemText>
							{t(Resources.Goals.Detail.Actions.reactivate)}
						</ListItemText>
					</MenuItem>
				) : (
					<MenuItem onClick={() => handleAction("complete")} sx={menuItemSx}>
						<ListItemIcon>
							<CircleCheckBig size={theme.iconSize.sm} />
						</ListItemIcon>
						<ListItemText>
							{t(Resources.Goals.Detail.Actions.complete)}
						</ListItemText>
					</MenuItem>
				)}
				<Divider />
				<MenuItem onClick={() => handleAction("delete")} sx={menuItemSx}>
					<ListItemIcon>
						<Trash2 size={theme.iconSize.sm} color={theme.palette.error.main} />
					</ListItemIcon>
					<ListItemText
						slotProps={{ primary: { color: theme.palette.error.main } }}
					>
						{t(Resources.Goals.Detail.Actions.delete)}
					</ListItemText>
				</MenuItem>
			</Menu>

			<GoalActionDialogs
				dialog={actions.dialog}
				goalName={goal.name}
				isChangingState={actions.isChangingState}
				isDeleting={actions.isDeleting}
				onComplete={actions.complete}
				onReactivate={actions.reactivate}
				onDelete={actions.remove}
				onCancel={actions.closeDialog}
			/>
		</>
	);
};
